// Le Sablier en plein écran (D-167 ; maquettes S3 à S6) : la tablette au bord du terrain, recopiée sur la TV. Un sablier
// par équipe (le sable aux couleurs de sa chasuble, les montants à la couleur du club), le temps qui reste en très grand,
// la carte en cours, « STOP · fait ! » et « Raté ». « Même carte pour tous » (par défaut) : la carte en grand au-dessus des
// sabliers, la suivante quand toutes ont fini ; « Chacune sa carte » : chacune la sienne, à son rythme. Une carte qui rend
// du temps (S4) : le tampon « Fait ! », +20 s qui s'envole, le sable remonte. Le zéro (S5) : la partie s'arrête pour tout le
// monde, les autres sabliers se figent. Le bilan (S6). L'arbitrage du coach : un appui long de 1,5 s sur une équipe (ou
// « Arbitrer » en haut), sans code (D-174) ; ou son téléphone. Les téléphones des équipes (game-live-run.ts) : la base fait
// foi. D-174 : l'appareil qui fait tourner la partie gère TOUTES les équipes, avec ou sans leurs téléphones ; sur un
// téléphone (ou une tablette en portrait), chaque équipe tient sur une ligne (son sable, son temps, STOP et Raté).
import './game.css';
import './game2.css';
import { fmtGameTime, gameMs, paused, recentLog } from '../domain/game';
import { FAM_HEX, cardTags, cardTitle, okText, type Card } from '../domain/game-cards';
import {
  GRACE_MS, READ_MS, aboutMin, changeCard, endGame, fail, invalidate, jokerChange, lastActionText, lastStop, nameOfS, newSablier, nextDeadline, pause, reading, sBilan,
  sRanking, sRunResult, sablierParticipants, sanction, sandAt, sandNow, stop, tick, undo, zeroAt,
  type SFx, type SStep, type SablierConfig, type SablierState,
} from '../domain/sablier';
import { sablierView } from '../domain/game-live';
import type { TeamGender } from '../domain/labels';
import type { LiveAction } from '../lib/game-live-api';
import { noteGameRun } from '../lib/games-api';
import { engage } from './drill-engine';
import { cardHtml, clubLogo, confetti, esc, famPicto, hourglass, inSentence, jersey, miniCard, type Kit } from './game-art';
import { COMPACT, ICON, countdown, drawJoin, flash, openLive, type LiveCtl, type LiveTeamSeat } from './game-live-run';
import { setGameSound, sndBonus, sndFanfare, sndFlip, sndKo, sndOk } from './game-sounds';
import { BIB_HEX, BIB_INK } from '../domain/groups';
import { BIB_LETTER } from '../domain/parcours';

export interface SablierRunInput {
  cfg: SablierConfig;
  title: string;
  teamLabel: string;
  gender: TeamGender;
  clubId: string;
  teamId: string | null;
  gameId: string | null;
  names: Map<string, string>;
  keep?: () => Promise<string | null>;
  onClose?: () => void;
}

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
/** Un téléphone, ou une tablette tenue en portrait : une ligne par équipe (game2.css). */
const compact = () => { try { return matchMedia(COMPACT).matches; } catch { return false; } };
const IC = {
  pause: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>`,
  play: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4v16l13-8z"/></svg>`,
  ban: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M5.6 5.6l12.8 12.8"/></svg>`,
  flag: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 21V4M5 4h11l-2 4 2 4H5"/></svg>`,
  undo: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 14L4 9l5-5"/><path d="M4 9h10a6 6 0 0 1 0 12h-3"/></svg>`,
  stopq: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>`,
  book: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z"/><path d="M4 19V5M8 7h7"/></svg>`,
};
/** « 2:58 » (sans dixièmes ; sous 10 s, les dixièmes). */
const clockTxt = (ms: number) => (ms > 0 && ms < 10_000 ? `0:0${(Math.floor(ms / 100) / 10).toFixed(1)}` : fmtGameTime(Math.ceil(ms / 1000) * 1000));
const mmss = (ms: number) => { const s = Math.round(ms / 1000); return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')}`; };
const plusMin = (ms: number) => `+${fmtGameTime(ms)}`;
/** « des Bleues » / « de Kenza » */
const ofName = (name: string) => (name.startsWith('Les ') ? `des ${name.slice(4)}` : `de ${name}`);

export function runSablier(inp: SablierRunInput): void {
  const cfg = inp.cfg;
  setGameSound(cfg.sound);
  const reduce = (() => { try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } })();
  const T = (ms: number) => (reduce ? Math.round(ms / 3) : ms);
  const participants = sablierParticipants(cfg);
  const kitOf = new Map(participants.map(p => [p.id, { color: p.color, ink: p.ink, letter: p.letter } as Kit]));
  const reserveOf = (id: string) => (cfg.teams[Number(id.slice(1))]?.reserve_s ?? cfg.reserve_s) * 1000;
  const same = cfg.mode === 'same';
  let live: LiveCtl | null = null;
  const clock = () => (live ? live.now() : Date.now());
  let gameId = inp.gameId;
  let g: SablierState | null = null;
  let seq = 0;
  let coach: { who: string | null; until: number } | null = null;
  let stopArmed = false;
  let noted = false;
  let endPhase: 'none' | 'zero' | 'end' = 'none';
  let anim: { who: string; until: number; html: string } | null = null;
  const queue: LiveAction[] = [];
  let joinDraw: (() => void) | null = null;
  let pulledFor = -1;
  let waitPull = false;

  const host = document.createElement('div');
  host.className = 'gm-tv sl-tv';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-label', 'Le Sablier');
  document.body.append(host);
  const release = engage(host, 'Le Sablier');
  host.innerHTML = `<div class="gm-top"></div><div class="sl-main" style="--n:${participants.length}"></div>`;
  const top = host.querySelector<HTMLElement>('.gm-top')!;
  let main = host.querySelector<HTMLElement>('.sl-main')!;

  const membersLine = (id: string) => participants.find(x => x.id === id)!.members.map(m => inp.names.get(m)).filter(Boolean).join(' · ');
  const name = (id: string | null) => (id && g ? nameOfS(g, id) : '');
  const now = () => clock();
  const t = () => (g ? gameMs(g, now()) : 0);

  // --- Le haut ----------------------------------------------------------------------------------------------------
  const drawTop = () => {
    const L = clubLogo();
    const over = !!g?.over;
    const sub = !g ? `par équipe · avant le coup d’envoi` : over ? 'terminé' : g && paused(g) ? 'en pause' : `réserve de départ ${Math.round(cfg.reserve_s / 60)} min`;
    const big = !g ? fmtGameTime(cfg.reserve_s * 1000) : fmtGameTime(g.shared.endedAt ?? gameMs(g, now()));
    const ph = live && g ? `<button type="button" class="ph" data-phones>${ICON.phone}Téléphones · ${live.online()}</button>` : '';
    const coachLeft = coach ? Math.max(0, Math.ceil((coach.until - now()) / 1000)) : 0;
    top.innerHTML = `<div class="l">${L ? `<img src="${esc(L)}" alt="">` : ''}<span><b>Le Sablier</b><small>${esc([inp.title, inp.teamLabel].filter(Boolean).join(' · '))}</small></span></div>
      <div class="c"><b data-clock>${big}</b><small>${esc(sub)}</small></div>
      <div class="r">${over ? `<button type="button" data-lib>${IC.book}Ma bibliothèque</button>`
        : !g ? `<button type="button" data-coach>${IC.flag}Arbitrer</button>`
        : `${ph}<button type="button" data-pause>${paused(g) ? `${IC.play}Reprendre` : `${IC.pause}Pause`}</button>
           <button type="button" data-coach class="${coach ? 'on' : ''}">${coach ? `${IC.flag}Arbitrage · ${coachLeft} s` : `${IC.flag}Arbitrer`}</button>`}</div>`;
  };

  // --- Une colonne d'équipe ---------------------------------------------------------------------------------------
  const glassOf = (id: string) => {
    const s = g!.ps[id]!;
    const tt = g!.shared.endedAt ?? t();
    const sand = Math.max(0, sandAt(s, tt));
    const flowing = !g!.over && !paused(g!) && s.flowFrom !== null && tt >= s.flowFrom && s.phase === 'defi';
    const k = kitOf.get(id)!;
    const gainUp = anim?.who === id && anim.until > Date.now() && /sl-plus(?!.*ko)/.test(anim.html);
    return hourglass(k.color, sand / Math.max(1, reserveOf(id)), { flow: flowing, up: gainUp, label: `Le sablier ${ofName(name(id))}` });
  };
  const timeOf = (id: string) => {
    const s = g!.ps[id]!;
    const tt = g!.shared.endedAt ?? t();
    const sand = Math.max(0, sandAt(s, tt));
    const ended = g!.over;
    const read = reading(s, tt);
    const sub = s.phase === 'out' ? 'plus de sable' : ended ? 'figé' : paused(g!) ? 'en pause' : read ? 'le sable attend' : s.phase === 'wait' ? 'arrêté' : sand < 60_000 ? 'moins d’une minute' : 'le sable coule';
    const cls = s.phase === 'out' ? 'zero' : sand < 60_000 && !ended ? 'low' : '';
    return { html: `<b>${clockTxt(sand)}</b><small>${sub}</small>`, cls };
  };
  const column = (id: string) => {
    const p = participants.find(x => x.id === id)!;
    const s = g!.ps[id]!;
    const tt = t();
    const ph = live?.phoneOf(id) ?? null;
    const read = reading(s, tt);
    const sand = Math.max(0, sandAt(s, tt));
    const ended = g!.over;
    const cls = [s.phase === 'out' ? 'zero' : '', ended && s.phase !== 'out' ? 'frozen' : '', !ended && sand < 60_000 && s.phase !== 'out' ? 'low' : '', anim?.who === id ? 'glow' : '',
      anim && anim.who !== id ? 'dim' : '', read && !ended ? 'glow' : ''].filter(Boolean).join(' ');
    const badge = ended ? (s.phase === 'out' ? `<span class="sl-badge red">buzzer</span>` : `<span class="sl-badge ice">figé</span>`)
      : read ? `<span class="sl-badge"><span class="ring" data-read>${Math.max(1, Math.ceil((s.flowFrom! - tt) / 1000))}</span>lecture</span>`
      : s.last && tt - s.last.at < 3000 && s.last.gain ? `<span class="sl-badge gold">+${s.last.gain / 1000} s</span>` : '';
    let st = '', stc = 'idle', body = '';
    const btns = (dis: boolean) => `<div class="gm-btns"><button type="button" class="gm-btn ok" data-stop="${id}" ${dis ? 'disabled' : ''}>${ICON.stop}Stop · fait !</button>
      <button type="button" class="gm-btn no" data-fail="${id}" ${dis ? 'disabled' : ''}>Raté</button></div>`;
    const dis = ended || paused(g!) || read;
    const offHint = ph === 'off' ? `<p class="gm-hint">Leur téléphone ne répond plus : STOP se touche ici.</p>` : '';
    const sub = (c: Card) => [...cardTags(c), c.sec ? `réussi : +${c.sec} s` : 'réussi : stop'].join(' · ');
    if (ended) {
      st = s.phase === 'out' ? 'À zéro' : 'Partie arrêtée'; stc = s.phase === 'out' ? 'ko' : 'idle';
    } else if (s.phase === 'defi' && s.card) {
      st = read ? `Nouvelle carte <em>· 5 s pour la lire</em>` : `En défi <em data-since>depuis ${fmtGameTime(tt - (s.flowFrom ?? tt))}</em>`;
      stc = read ? 'idle' : '';
      const own = !same || (g!.shared.round && s.cardIdx !== g!.shared.round.idx);
      const jk = s.jokers.includes('change') ? `<button type="button" class="gm-btn joker" data-jk-change="${id}" ${dis ? 'disabled' : ''}>Joker : change de défi</button>` : '';
      body = `${offHint}${own ? miniCard(s.card, sub(s.card), 'sablier') : ''}${btns(dis)}${jk}`;
    } else if (s.phase === 'wait') {
      st = s.note ?? 'Fait'; stc = s.noteKo ? 'ko' : 'ok';
      body = same ? `<div class="gm-slot" style="--c:${p.color}">${g!.participants.every(x => g!.ps[x.id]!.phase !== 'defi') ? 'la carte suivante se retourne' : 'on attend les autres'}</div>`
        : `<div class="gm-slot" style="--c:${p.color}">la carte suivante se retourne</div>`;
    } else { st = 'À zéro'; stc = 'ko'; }
    const phChip = ph === 'on' ? `<span class="sl-ph"><i></i>${ICON.phone}relié</span>` : ph === 'off' ? `<span class="sl-ph off"><i></i>${ICON.phone}hors réseau</span>` : '';
    const tm = timeOf(id);
    // Le zéro (S5) : le bandeau passe volontairement par-dessus les colonnes, 3 s (data-lyt-ok, scripts/layout/measure.mjs).
    return `<section class="sl-col ${cls}" data-team="${id}" ${endPhase === 'zero' ? 'data-lyt-ok' : ''} style="--c:${p.color};--ci:${p.ink}">
      <div class="sl-ch">${jersey(kitOf.get(id)!, 40)}<span><b>${esc(p.name)}</b>${membersLine(id) ? `<small>${esc(membersLine(id))}</small>` : ''}</span>${phChip}</div>
      <div class="sl-glass" data-glass="${id}">${glassOf(id)}${badge}</div>
      <div class="sl-time ${tm.cls}" data-time="${id}">${tm.html}</div>
      <div class="gm-st ${stc}">${st}</div>
      <div class="sl-do">${body}</div></section>`;
  };
  /** « Même carte pour tous » : la carte en grand au-dessus des sabliers. */
  const sameCard = () => {
    const r = g!.shared.round;
    if (!r) return '';
    const tt = t();
    const read = tt < r.from;
    const left = r.until !== null ? Math.max(0, r.until - tt) : null;
    const w = Math.min(150, Math.round((main.clientHeight || 600) * .22));
    // Sur un téléphone : la carte en petit (son pictogramme), pour que toutes les équipes tiennent dessous.
    const art = compact() ? `<span class="sl-same-ic" style="--fam:${FAM_HEX[r.card.fam ?? 'tir']}">${famPicto(r.card.fam ?? 'tir', 22)}</span>` : cardHtml(r.card, w, { game: 'sablier' });
    return `<div class="sl-same" data-same>${art}
      <span class="tx"><small>Carte ${r.n} · pour toutes les équipes</small><b>${esc(cardTitle(r.card))}</b><p>${esc(okText(r.card))}</p></span>
      <span class="left" data-left>${read ? `<b>${Math.max(1, Math.ceil((r.from - tt) / 1000))}</b>s pour la lire` : left !== null ? `<b>${fmtGameTime(left)}</b>reste au plus` : `<b>∞</b>sans limite`}</span></div>`;
  };

  // --- L'arbitrage (sur l'appareil de la partie : appui long, ou « Arbitrer » ; sans code, D-174) ---------------------
  const coachPanel = () => {
    const who = coach!.who;
    const p = who ? participants.find(x => x.id === who)! : null;
    const ls = who && g ? lastStop(g, who) : null;
    const la = g ? lastActionText(g) : null;
    const log = g ? recentLog(g, 5).map(e => {
      const c = e.who ? kitOf.get(e.who)?.color ?? '#9AA1AA' : '#9AA1AA';
      return `<li><time>${fmtGameTime(e.at)}</time><i style="background:${c}"></i><span>${e.who ? `<b>${esc(name(e.who))}</b> · ` : '<b>Coach</b> · '}${esc(e.text)}</span></li>`;
    }).join('') : '';
    return `<section class="gm-coach sl-coach" data-coachpanel>
      <div class="h">${p ? jersey(kitOf.get(p.id)!, 40) : `<span></span>`}<span><small>Arbitrer</small><b>${esc(p ? p.name : 'La partie')}</b></span><button type="button" class="x" data-coach-x aria-label="Fermer l’arbitrage">×</button></div>
      ${p ? `<button type="button" class="gm-crow main" data-invalidate ${ls ? '' : 'disabled'}><span class="ico">${IC.ban}</span><span><strong>Invalider ce STOP</strong>
          <small>${ls ? `« ${esc(ls.title)} », à ${fmtGameTime(ls.at)} : la carte revient, le sable recoule depuis maintenant` : 'Aucun STOP à invalider'}</small></span></button>
        <div class="gm-crow plain"><span class="ico">${IC.flag}</span><span><strong>Sanction</strong><span class="chips"><button type="button" data-sanction="15">−15 s</button><button type="button" data-sanction="30">−30 s</button></span></span></div>
        <button type="button" class="gm-crow" data-change><span class="ico">${ICON.swap}</span><span><strong>Changer leur carte</strong><small>Carte injouable (matériel, blessure) : une autre, sans pénalité.</small></span></button>` : ''}
      <button type="button" class="gm-crow" data-undo ${la ? '' : 'disabled'}><span class="ico">${IC.undo}</span><span><strong>Annuler la dernière action</strong>
        <small>${la ? `la dernière : ${esc(la.text)}, à ${fmtGameTime(la.at)}` : 'Rien à annuler'}</small></span></button>
      <button type="button" class="gm-crow" data-pauseall><span class="ico">${g && paused(g) ? IC.play : IC.pause}</span><span><strong>${g && paused(g) ? 'Reprendre' : 'Pause pour tous'}</strong>
        <small>${g && paused(g) ? 'le sable recoule' : 'le sable s’arrête partout'}</small></span></button>
      ${!p ? `<button type="button" class="gm-crow" data-stop-all><span class="ico">${IC.stopq}</span><span><strong>${stopArmed ? 'Arrêter ? Touche encore' : 'Arrêter la partie'}</strong>
        <small>le plus de sable gagne, puis le bilan</small></span></button>` : ''}
      <div class="gm-log"><p>Les dernières actions</p><ol>${log || '<li><span>Rien encore.</span></li>'}</ol></div></section>`;
  };
  const openCoach = (who: string | null) => {
    coach = { who, until: now() + 30_000 };
    render();
  };
  const touchCoach = () => { if (coach) coach.until = now() + 30_000; };

  // --- Le rendu ---------------------------------------------------------------------------------------------------
  function publish() {
    if (!live || !g) return;
    live.publish(sablierView(g, { seq: ++seq, t: g.shared.endedAt ?? gameMs(g, now()), reserve: cfg.reserve_s }), g.over ? 'over' : 'running');
  }
  function render() {
    drawTop();
    if (!g) return;
    if (endPhase === 'end') return;
    main.className = `sl-main ${same ? 'same' : ''} ${g.over ? 'sl-ended' : ''} ${coach ? 'arb' : ''}`;
    main.innerHTML = `${same ? sameCard() : ''}${participants.map(p => column(p.id)).join('')}${coach ? coachPanel() : ''}${anim && anim.until > Date.now() ? anim.html : ''}`;
    if (paused(g) && !g.over) main.insertAdjacentHTML('beforeend', `<div class="gm-paused"><b>Pause</b><button type="button" class="gm-btn ok" data-resume>${IC.play}Reprendre</button></div>`);
    if (endPhase === 'zero') drawZero();
    publish();
  }
  /** Le sable qui coule : seulement les sabliers et les temps (pas les boutons). */
  function refresh() {
    if (!g || endPhase === 'end') return;
    const tt = t();
    for (const p of participants) {
      const gl = main.querySelector(`[data-glass="${p.id}"]`);
      if (gl) { const badge = gl.querySelector('.sl-badge'); gl.innerHTML = glassOf(p.id); if (badge) gl.append(badge); }
      const ring = main.querySelector(`[data-glass="${p.id}"] [data-read]`);
      const s = g.ps[p.id]!;
      if (ring && s.flowFrom !== null) ring.textContent = String(Math.max(1, Math.ceil((s.flowFrom - tt) / 1000)));
      const tm = main.querySelector<HTMLElement>(`[data-time="${p.id}"]`);
      if (tm) { const x = timeOf(p.id); tm.innerHTML = x.html; tm.className = `sl-time ${x.cls}`; }
      const since = main.querySelector(`[data-team="${p.id}"] [data-since]`);
      if (since && s.flowFrom !== null) since.textContent = `depuis ${fmtGameTime(tt - s.flowFrom)}`;
    }
    const left = main.querySelector('[data-left]');
    const r = g.shared.round;
    if (left && r) left.innerHTML = tt < r.from ? `<b>${Math.max(1, Math.ceil((r.from - tt) / 1000))}</b>s pour la lire` : r.until !== null ? `<b>${fmtGameTime(Math.max(0, r.until - tt))}</b>reste au plus` : `<b>∞</b>sans limite`;
  }

  // --- Les effets -------------------------------------------------------------------------------------------------
  function show(step: SStep, sound?: () => void) {
    const before = g;
    g = step.g;
    if (step.g === before && !step.fx.length) return;
    sound?.();
    for (const f of step.fx) {
      if (f.t === 'stop') {
        sndOk();
        const p = participants.find(x => x.id === f.who)!;
        const col = main.querySelector<HTMLElement>(`[data-team="${f.who}"]`);
        const left = col ? Math.max(12, col.offsetLeft + (col.offsetWidth - 300) / 2) : 30;
        const w = compact() ? 120 : Math.min(260, Math.round((main.clientHeight || 600) * .42));
        anim = { who: f.who, until: Date.now() + T(f.gain ? 2600 : 1500), html: `<div class="sl-fly" style="left:${left}px;top:24px;--c:${p.color};--ci:${p.ink}">
          <div class="sl-fly-head">${jersey(kitOf.get(f.who)!, 32)}<b>${esc(p.name)}</b><span>stop en ${fmtGameTime(f.ms)}</span></div>
          <div class="sl-fly-stage">${cardHtml(f.card, w, { game: 'sablier' })}<span class="sl-stamp">Fait !</span>${f.gain ? `<span class="sl-plus">+${f.gain / 1000}<small>secondes</small></span>` : ''}</div>
          <p class="sl-fly-foot">${f.gain ? `Réussi : ${f.gain / 1000} s de sable reviennent dans le sablier ${esc(ofName(p.name))}.` : `Le sable ${esc(ofName(p.name))} s’arrête.`}</p></div>` };
        if (f.gain) sndBonus();
      } else if (f.t === 'loss' && f.why === 'Raté') {
        sndKo();
        const p = participants.find(x => x.id === f.who)!;
        const col = main.querySelector<HTMLElement>(`[data-team="${f.who}"]`);
        const left = col ? Math.max(12, col.offsetLeft + (col.offsetWidth - 300) / 2) : 30;
        const card = before?.ps[f.who]?.card;
        const w = compact() ? 110 : Math.min(220, Math.round((main.clientHeight || 600) * .36));
        anim = { who: f.who, until: Date.now() + T(1600), html: `<div class="sl-fly" style="left:${left}px;top:24px;--c:${p.color};--ci:${p.ink}">
          <div class="sl-fly-head">${jersey(kitOf.get(f.who)!, 32)}<b>${esc(p.name)}</b><span>raté</span></div>
          <div class="sl-fly-stage">${card ? cardHtml(card, w, { game: 'sablier' }) : ''}<span class="sl-stamp ko">Raté</span><span class="sl-plus ko">−20<small>secondes</small></span></div></div>` };
      } else if (f.t === 'card' && f.who === null) sndFlip();
      else if (f.t === 'gain' && f.why === 'Sablier retourné') sndBonus();
      else if (f.t === 'zero') startZero(f.who);
    }
    if (anim) setTimeout(() => { if (anim && anim.until <= Date.now() + 30) { anim = null; render(); } }, Math.max(0, anim.until - Date.now()) + 40);
    if (g.over && endPhase === 'none') startZero(g.shared.zeroBy);
    render();
  }

  // --- Le zéro (S5), puis le bilan (S6) ---------------------------------------------------------------------------
  function startZero(who: string | null) {
    if (endPhase !== 'none') return;
    endPhase = 'zero';
    anim = null;
    coach = null;
    if (who) sndKo();
    note();
    void (async () => { await sleep(T(3600)); if (!host.isConnected) return; endPhase = 'end'; drawEnd(); })();
  }
  function drawZero() {
    const z = g!.shared.zeroBy;
    const tEnd = g!.shared.endedAt ?? 0;
    const others = participants.filter(p => p.id !== z).map(p => fmtGameTime(sandNow(g!, p.id, now())));
    const zp = z ? participants.find(p => p.id === z)! : null;
    const eb = zp ? `${fmtGameTime(tEnd)} · le dernier grain est tombé` : g!.shared.endedBy === 'time' ? `${fmtGameTime(tEnd)} · fin du temps` : `${fmtGameTime(tEnd)} · partie arrêtée`;
    const h2 = zp ? 'Plus de sable !' : 'Fin de la partie';
    const l = zp ? `${zp.name} ${zp.name.startsWith('Les ') ? 'sont' : 'est'} à zéro : la partie s’arrête pour tout le monde. ${others.length > 1 ? `Les autres sabliers sont figés à ${others.slice(0, -1).join(', ')} et ${others[others.length - 1]}.` : `L’autre sablier est figé à ${others[0]}.`}`
      : 'Les sabliers sont figés : le plus de sable gagne.';
    main.insertAdjacentHTML('beforeend', `${zp ? '<div class="sl-flash" data-lyt-ok></div>' : ''}<div class="sl-end" role="status" data-lyt-ok>${confetti(['#EF4444', '#3B82F6', '#FACC15', '#F28C28'], 1240, 240, 70)}
      ${zp ? `<div class="who">${jersey(kitOf.get(zp.id)!, 110, 'big')}<b>${esc(zp.name)}</b></div>` : '<span></span>'}
      <div><p class="eb">${esc(eb)}</p><h2>${h2}</h2><p class="l">${esc(l)}</p></div>
      <div class="nx"><span class="ring">3</span><span>le classement<br>dans 3 s</span></div></div>`);
    main.querySelector('.sl-end > .gm-conf')?.setAttribute('data-lyt-ok', '');
  }
  function note() {
    if (noted || !g) return;
    noted = true;
    const r = sRunResult(g, cfg, now());
    void noteGameRun({ game_id: gameId, club_id: inp.clubId, team_id: inp.teamId, mode: 'teams', duration_s: r.duration_s, result: r.result, kind: 'sablier' }).catch(() => undefined);
  }
  function drawEnd() {
    if (!g) return;
    coach = null;
    drawTop();
    publish();
    const rk = sRanking(g, now());
    const first = rk[0]!;
    const fp = participants.find(p => p.id === first.id)!;
    const z = g.shared.zeroBy;
    const zName = z ? inSentence(name(z)) : '';
    const tEnd = mmss(g.shared.endedAt ?? 0);
    const eb = z ? `Fin · ${tEnd} · ${zName} à zéro` : g.shared.endedBy === 'time' ? `Fin du temps · ${tEnd}` : `Partie arrêtée · ${tEnd}`;
    const plural = fp.name.startsWith('Les ');
    const best = Math.max(1, ...rk.map(r => r.sand));
    const rows = rk.map(r => {
      const p = participants.find(x => x.id === r.id)!;
      return `<div class="${r.rank === 1 ? 'first' : ''} ${r.out ? 'dry' : ''}" style="--c:${p.color}"><em>${r.rank}</em>${jersey(kitOf.get(p.id)!, 40)}<span><b>${esc(p.name)}</b><span class="bar"><i style="width:${Math.round(r.sand / best * 100)}%"></i></span></span><small><b>${fmtGameTime(r.sand)}</b>${r.out ? 'à sec' : 'de sable'}</small></div>`;
    }).join('');
    const b = sBilan(g);
    const tr = b.rows.map(r => { const p = participants.find(x => x.id === r.id)!; return `<tr><td><span><i style="background:${p.color}"></i>${esc(p.name.replace(/^Les /, ''))}</span></td><td class="ok">${r.ok}</td><td class="ko">${r.ko}</td><td>${r.avgMs !== null ? fmtGameTime(r.avgMs) : '—'}</td><td>${r.gained ? plusMin(r.gained) : '—'}</td></tr>`; }).join('');
    const miss = b.costly.map(m => `<li style="--fam:${FAM_HEX[m.card.fam ?? 'tir']}"><i>${famPicto(m.card.fam ?? 'tir', 17)}</i><span>${esc(cardTitle(m.card))}</span><b>${fmtGameTime(m.avgMs)}</b></li>`).join('');
    const c0 = b.costly[0];
    const learn = c0 ? `<div class="gm-learn"><b>« ${esc(cardTitle(c0.card))} » coûte cher</b> : ${mmss(c0.avgMs)} en moyenne${c0.ko ? `, ${c0.ko} raté${c0.ko > 1 ? 's' : ''} sur ${c0.tries}` : ''}.${b.misses ? ` ${esc(name(b.misses.id))} ${name(b.misses.id).startsWith('Les ') ? 'ont' : 'a'} perdu ${mmss(b.misses.ms).replace(' 00', '')} sur ${b.misses.ko} ratés.` : ''} Une idée pour la prochaine séance.</div>` : '';
    main.className = 'gm-main gm-end';
    main.style.gridTemplateColumns = 'minmax(0, 1fr) 470px';
    main.innerHTML = `<div class="gm-win sl-win" data-win>${confetti(participants.map(p => p.color))}
        <p class="eb">${esc(eb)}</p><div class="hgs">${hourglass(fp.color, first.sand / Math.max(1, reserveOf(fp.id)), { w: 74 })}${jersey(kitOf.get(fp.id)!, 132, 'big')}</div>
        <h2>${esc(fp.name)}</h2><h3>${plural ? 'gardent' : 'garde'} le plus de sable</h3>
        <div class="gm-rank">${rows}</div>
        <div class="acts"><button type="button" class="p" data-again>Rejouer</button>${gameId ? '' : `<button type="button" class="g" data-keep>Garder ce jeu</button>`}<button type="button" class="g" data-close>Fermer</button></div></div>
      <section class="gm-bilan"><p class="eb">Pour le coach</p><h3>Ce que la partie dit</h3>
        <table><tr><th>Équipe</th><th>Défis réussis</th><th>ratés</th><th>par défi</th><th>sable rendu</th></tr>${tr}</table>
        ${miss ? `<p class="eb" style="color:#737A83">Les cartes qui ont coûté le plus de sable</p><ul class="gm-miss">${miss}</ul>` : ''}
        ${learn}
        <p class="gm-kept">Gardé : la partie, sa durée et, par équipe, le sable restant, les défis réussis et ratés. Rien par personne, rien des téléphones.</p></section>`;
    main.querySelector('.gm-win > .gm-conf')?.setAttribute('data-lyt-ok', '');
    sndFanfare();
  }

  // --- Les gestes (la tablette, les téléphones, le coach) ---------------------------------------------------------
  const rnd = () => Math.random();
  /** Un geste d'une équipe ou du coach, à l'heure `at` (celle de la base pour un téléphone). */
  function doAct(kind: string, seat: string | null, pl: Record<string, unknown>, at: number, fromCoachPhone = false) {
    if (!g) return;
    const before = g;
    if (kind === 'stop' && seat) show(stop(g, seat, rnd, at));
    else if (kind === 'fail' && seat) show(fail(g, seat, rnd, at, { joker: pl.joker === 'cancel' }));
    else if (kind === 'joker' && seat && pl.joker === 'change') show(jokerChange(g, seat, rnd, at), sndFlip);
    else if (kind === 'invalidate' && seat) show(invalidate(g, seat, at), sndKo);
    else if (kind === 'sanction' && seat) show(sanction(g, seat, pl.value === '30' ? 30 : 15, at), sndKo);
    else if (kind === 'change' && seat) show(changeCard(g, seat, rnd, at), sndFlip);
    else if (kind === 'pause') { g = pause(g, pl.on === undefined ? !paused(g) : pl.on === true, at); render(); }
    else if (kind === 'undo') { g = undo(g, at); render(); }
    else if (kind === 'end') { g = endGame(g, 'coach', at); show({ g, fx: [] }); if (g.over && endPhase === 'none') startZero(null); }
    else if (kind === 'qr') openJoin(true);
    if (fromCoachPhone && g !== before) flash(host, `${['stop', 'fail', 'joker'].includes(kind) ? 'Télécommande de' : 'Arbitré par'} ${live?.coachName() ?? 'le coach'}${seat ? ` · ${name(seat)}` : ''}`);
  }
  /** Les gestes des téléphones, dans l'ordre de la base ; un STOP compte à l'heure où la base l'a reçu. */
  function drain() {
    while (queue.length && g && endPhase === 'none') {
      const a = queue.shift()!;
      const at = Math.min(a.at, clock() + 500);
      if (a.seat === 'coach') {
        const seat = typeof a.payload.seat === 'string' ? a.payload.seat : null;
        doAct(a.kind, seat, a.payload, at, true);
      } else doAct(a.kind, a.seat, a.payload, at);
    }
  }

  host.addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('button');
    if (!b || (b as HTMLButtonElement).disabled) return;
    const d = b.dataset;
    const at = now();
    if (d.stop) doAct('stop', d.stop, {}, at);
    else if (d.fail) doAct('fail', d.fail, { joker: g?.ps[d.fail]?.jokers.includes('cancel') ? 'cancel' : undefined }, at);
    else if (d.jkChange) doAct('joker', d.jkChange, { joker: 'change' }, at);
    else if (b.hasAttribute('data-pause') || b.hasAttribute('data-resume')) doAct('pause', null, {}, at);
    else if (b.hasAttribute('data-coach')) { if (coach) { coach = null; stopArmed = false; render(); } else openCoach(null); }
    else if (b.hasAttribute('data-coach-x')) { coach = null; stopArmed = false; render(); }
    else if (b.hasAttribute('data-invalidate') && coach?.who) { touchCoach(); doAct('invalidate', coach.who, {}, at); }
    else if (d.sanction && coach?.who) { touchCoach(); doAct('sanction', coach.who, { value: d.sanction }, at); }
    else if (b.hasAttribute('data-change') && coach?.who) { touchCoach(); doAct('change', coach.who, {}, at); }
    else if (b.hasAttribute('data-undo')) { touchCoach(); doAct('undo', null, {}, at); }
    else if (b.hasAttribute('data-pauseall')) { touchCoach(); doAct('pause', null, {}, at); }
    else if (b.hasAttribute('data-stop-all')) {
      touchCoach();
      if (!stopArmed) { stopArmed = true; render(); setTimeout(() => { stopArmed = false; if (coach && host.isConnected) render(); }, 4000); return; }
      stopArmed = false; doAct('end', null, {}, at);
    }
    else if (b.hasAttribute('data-phones') && live) openJoin(true);
    else if (b.hasAttribute('data-join-back')) closeJoin();
    else if (b.hasAttribute('data-join-none')) { const l = live; live = null; closeJoin(); void l?.close(); void kickoff(); }
    else if (b.hasAttribute('data-join-go')) { closeJoin(); void kickoff(); }
    else if (b.hasAttribute('data-again')) restart();
    else if (b.hasAttribute('data-keep')) void (async () => { b.setAttribute('disabled', ''); b.textContent = 'Gardé…'; gameId = (await inp.keep?.().catch(() => null)) ?? gameId; b.textContent = gameId ? 'Gardé ✓' : 'Impossible'; })();
    else if (b.hasAttribute('data-close') || b.hasAttribute('data-lib')) close(b.hasAttribute('data-lib'));
  });

  // L'appui long de 1,5 s sur la colonne (ou la ligne) d'une équipe : son arbitrage, tout de suite.
  let press: { t: number; id: string; x: number; y: number } | null = null;
  const cancelPress = () => { if (press) { clearTimeout(press.t); main.querySelector('.pressing')?.classList.remove('pressing'); press = null; } };
  host.addEventListener('pointerdown', e => {
    const col = (e.target as HTMLElement).closest<HTMLElement>('.sl-col');
    if (!col || !g || g.over || (e.target as HTMLElement).closest('button')) return;
    cancelPress();
    col.classList.add('pressing');
    const id = col.dataset.team!;
    press = { id, x: e.clientX, y: e.clientY, t: window.setTimeout(() => { cancelPress(); openCoach(id); }, 1500) };
  });
  host.addEventListener('pointermove', e => { if (press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 12) cancelPress(); });
  for (const ev of ['pointerup', 'pointercancel', 'pointerleave']) host.addEventListener(ev, cancelPress);
  host.addEventListener('contextmenu', e => e.preventDefault());
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { if (g?.over) close(); else if (coach) { coach = null; stopArmed = false; render(); } else openCoach(null); } };
  document.addEventListener('keydown', onKey);
  // Le téléphone tourné, la fenêtre redimensionnée : la mise en page (colonnes ou lignes) change, on redessine.
  const onResize = () => { if (g && endPhase === 'none') render(); };
  window.addEventListener('resize', onResize);

  // L'horloge : le sable qui coule, le zéro (à l'heure de la base, 0,5 s de grâce), la carte suivante, l'arbitrage qui se referme.
  const timer = window.setInterval(() => {
    if (!host.isConnected || !g) return;
    const nw = now();
    if (coach && nw > coach.until) { coach = null; stopArmed = false; render(); return; }
    if (g.over || endPhase !== 'none') return;
    drain();
    const dl = nextDeadline(g, cfg.stop15);
    const gt = gameMs(g, nw);
    if (dl !== null && gt >= dl) {
      // Un zéro est passé : les téléphones ont-ils envoyé un STOP dans la grâce ? On relit la base d'abord (une fois).
      if (waitPull) return;
      if (live && pulledFor !== dl) { pulledFor = dl; waitPull = true; void live.host.pull().finally(() => { waitPull = false; drain(); }); return; }
      show(tick(g, nw, rnd, cfg.stop15));
      return;
    }
    if (same && g.shared.round && g.participants.every(p => g!.ps[p.id]!.phase !== 'defi')) { show(tick(g, nw, rnd, cfg.stop15)); return; }
    const clockEl = top.querySelector('[data-clock]');
    if (clockEl) clockEl.textContent = fmtGameTime(gameMs(g, nw));
    if (coach) { const cb = top.querySelector('[data-coach]'); if (cb) cb.innerHTML = `${IC.flag}Arbitrage · ${Math.max(0, Math.ceil((coach.until - nw) / 1000))} s`; }
    // Une carte qui finit de se lire : les boutons s'allument.
    const wasRead = main.querySelectorAll('[data-read]').length;
    const reads = participants.filter(p => reading(g!.ps[p.id]!, gt)).length;
    if (wasRead !== reads && !anim) { render(); return; }
    refresh();
  }, 100);

  // --- Le début : les téléphones (T1) ou tout de suite ------------------------------------------------------------
  const seats: LiveTeamSeat[] = participants.map((p, i) => ({ seat: p.id, bib: cfg.teams[i]!.bib, kit: kitOf.get(p.id)!, name: p.name }));
  let joinBox: HTMLElement | null = null;
  function openJoin(reopen: boolean) {
    if (!live) return;
    closeJoin();
    joinBox = document.createElement('div');
    joinBox.className = 'sl-joinbox';
    host.append(joinBox);
    joinDraw = drawJoin(joinBox, live, { teams: seats, reopen, reserve: fmtGameTime(cfg.reserve_s * 1000), phones: cfg.phones, remote: cfg.remote });
  }
  function closeJoin() { joinBox?.remove(); joinBox = null; joinDraw = null; }
  async function kickoff() {
    await countdown(host, reduce);
    if (!host.isConnected) return;
    const st = newSablier(cfg, participants, rnd, clock());
    g = st.g;
    sndFlip();
    render();
  }
  function restart() {
    endPhase = 'none'; noted = false; coach = null; anim = null; queue.length = 0;
    main.className = 'sl-main'; main.style.gridTemplateColumns = '';
    void kickoff();
  }
  function close(toLibrary = false) {
    clearInterval(timer);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('resize', onResize);
    void live?.close();
    release();
    host.remove();
    inp.onClose?.();
    if (toLibrary && inp.teamId) location.hash = `#/basket/drills/${inp.teamId}`;
  }

  drawTop();
  main.innerHTML = `<div class="gm-paused" style="position:static;background:none"><b style="font-size:40px">≈ ${aboutMin(cfg.reserve_s)} min</b></div>`;
  if ((cfg.phones || cfg.remote) && inp.teamId) {
    void (async () => {
      live = await openLive({
        teamId: inp.teamId!, gameId, kind: 'sablier', title: inp.title, teams: seats,
        onAction: a => { queue.push(a); },
        onSeats: () => { joinDraw?.(); if (g && !joinBox) { const ph = top.querySelector('[data-phones]'); if (ph) ph.innerHTML = `${ICON.phone}Téléphones · ${live?.online() ?? 0}`; } },
        onToast: text => flash(host, text),
      });
      if (!host.isConnected) { void live?.close(); return; }
      if (!live) { flash(host, 'Les téléphones ne se relient pas : on joue sur la tablette.'); void kickoff(); return; }
      openJoin(false);
      live.publish({ ...sablierView(newSablier(cfg, participants, rnd, clock()).g, { seq: ++seq, t: 0, reserve: cfg.reserve_s, started: false }) }, 'lobby');
    })();
  } else void kickoff();
  void GRACE_MS; void READ_MS; void zeroAt;
}
